import React, { useEffect } from 'react';

export default function TermsOfService() {
  useEffect(() => {
    document.title = "Terms of Service - SongToSong";
    return () => {
      document.title = "SongToSong";
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1a1a1a] font-sans selection:bg-pink-100">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">Terms of Service for SongToSong</h1>
        <div className="text-sm text-gray-500 mb-10">
          <p><strong>Effective Date:</strong> April 3, 2026</p>
          <p><strong>Last Updated:</strong> April 3, 2026</p>
        </div>

        <p className="mb-8 leading-relaxed">
          Welcome to SongToSong! These Terms of Service ("Terms") govern your use of the website located at <a href="https://songtosong.app" className="text-[#ff0080] hover:underline">https://songtosong.app</a> ("the Service", "SongToSong"), operated by SongToSong ("we", "us", "our").
        </p>

        <p className="mb-12 leading-relaxed">
          By accessing or using SongToSong, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the Service.
        </p>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">1. Description of the Game</h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            SongToSong is an interactive daily music trivia and connection puzzle game where players connect starting and target musical artists through collaborative tracks, features, and discography links.
          </p>
          <p className="text-gray-700 leading-relaxed">
            The Service may be played as a guest or with a registered account to sync game statistics, streaks, and achievements across devices.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">2. Accounts and Security</h2>
          <ul className="list-disc pl-6 space-y-3 text-gray-700 leading-relaxed">
            <li>When creating an account, you must provide accurate, complete, and current information.</li>
            <li>You are responsible for safeguarding your password and account credentials.</li>
            <li>You agree not to disclose your password to any third party and to notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</li>
            <li>We reserve the right to terminate or suspend accounts that violate these Terms or engage in abusive or fraudulent behavior.</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">3. Third-Party Services & Spotify API</h2>
          <div className="bg-pink-50 p-6 rounded-2xl border border-pink-100 mb-6">
            <h3 className="text-lg font-bold mb-2 text-[#99004d]">Spotify API & Trademarks</h3>
            <p className="text-[#800040] text-sm leading-relaxed">
              SongToSong utilizes the Spotify Web API for search metadata, artist information, album covers, and audio previews. SongToSong is not endorsed, certified, or otherwise affiliated with Spotify AB. All trademarks, artist names, song titles, album artwork, and audio clips remain the intellectual property of their respective owners and rights holders.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">4. Custom Challenges & User Conduct</h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            Users may create and share custom challenge links. By using this feature, you agree:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
            <li>Not to use automated scripts, scrapers, bots, or exploit mechanisms to disrupt game servers or leaderboard integrity.</li>
            <li>Not to transmit or share links that promote harassment, hate speech, malware, or illegal material.</li>
            <li>Not to attempt to circumvent Cloudflare Turnstile, rate limits, or security filters.</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">5. Intellectual Property</h2>
          <p className="text-gray-700 leading-relaxed">
            The Service and its original content (excluding music metadata and artwork owned by third parties), features, and functionality are and will remain the exclusive property of SongToSong and its licensors.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">6. Disclaimer of Warranty & Limitation of Liability</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, whether express or implied.
          </p>
          <p className="text-gray-700 leading-relaxed">
            In no event shall SongToSong, its creators, or its affiliates be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your access to or use of the Service.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">7. Changes to Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            We reserve the right to modify or replace these Terms at any time. Changes become effective immediately upon posting to this page.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-b border-gray-100">8. Contact Us</h2>
          <p className="mb-4">If you have any questions about these Terms of Service, please contact us:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Email:</strong> <a href="mailto:contact@songtosong.com" className="text-[#ff0080] hover:underline">contact@songtosong.com</a></li>
            <li><strong>Website:</strong> <a href="https://songtosong.app" className="text-[#ff0080] hover:underline">https://songtosong.app</a></li>
          </ul>
        </section>

        <div className="mt-12">
          <a href="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#ff0080] hover:opacity-80 transition-opacity">
            ← Back to SongToSong
          </a>
        </div>
      </div>
    </div>
  );
}
